<template>
  <div class="feature-tree w-full h-full flex flex-col overflow-x-hidden">
    <div class="py-10px px-12px">
      <n-input
        v-model:value="searchQuery"
        size="small"
        placeholder="搜索"
        clearable
      />
    </div>

    <n-divider class="!m-0" />

    <div ref="panelContentRef" class="panel-content w-full overflow-x-hidden">
      <n-tree
        v-if="showTree || isTreeLoading"
        :data="displayedTreeData"
        :selected-keys="selectedKeys"
        :expanded-keys="expandedKeys"
        block-line
        selectable
        show-line
        :render-label="renderTreeNodeLabel"
        :node-props="buildTreeNodeProps"
        @update:selected-keys="handleSelect"
        @update:expanded-keys="handleExpandedKeysChange"
      />
      <n-empty
        v-else
        :description="emptyDescription"
        size="small"
        style="margin-top: 60px;"
      />
      <div
        v-if="isTreeLoading"
        class="feature-tree-loading feature-tree-loading-overlay"
      >
        <n-spin size="small" />
        <span class="feature-tree-loading-text">正在加载设计树…</span>
      </div>
    </div>

    <feature-tree-node-context-menu
      v-model:show="showContextMenu"
      :x="contextMenuX"
      :y="contextMenuY"
      :target-key="contextMenuTargetKey"
      :node-type="contextMenuNodeType"
      :plane-id="contextMenuPlaneId"
      :revision="contextMenuRevision"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * FeatureTree 组件。
 *
 * 文件定位：
 * 1. 该组件负责渲染设计树、搜索、右键菜单与基础节点交互；
 * 2. 节点语义、编辑能力与右键动作由 useFeatureTreeStore 提供，组件只负责视图映射；
 * 3. 这样后续新增可编辑特征类型时，只需要扩展节点元数据与 store 能力，不必重写组件。
 */
import { computed, h, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import {
  NDivider,
  NEmpty,
  NInput,
  NSpin,
  NTree,
} from 'naive-ui';
import type { TreeOption } from 'naive-ui';
import { storeToRefs } from 'pinia';
import type { GlbNodeRole } from '@puppycad/cad-core';
import { useFeatureTreeStore } from '@/hooks/useFeatureTree';
import {
  getFeatureTreeNodeKey,
  resolveFeatureTreeExpandedKeys,
} from '@/hooks/feature-tree/resolve-feature-tree-expanded-keys';
import {
  resolveFeatureRollbackDropTargetFromRows,
} from '@/hooks/feature-tree/resolve-feature-rollback-drop-index';
import type { FeatureRollbackDropTargetRow } from '@/hooks/feature-tree/resolve-feature-rollback-drop-index';
import {
  FeatureTreeNodeKind,
} from '@/hooks/feature-tree/feature-tree-types';
import type { FeatureTreeNodeMeta } from '@/hooks/feature-tree/feature-tree-types';
import FeatureTreeNodeIcon from '@/components/editor/FeatureTreeNodeIcon.vue';
import FeatureTreeNodeContextMenu from '@/components/editor/FeatureTreeNodeContextMenu.vue';
import FeatureRollbackBarLabel from '@/components/editor/FeatureRollbackBarLabel.vue';
import { useVersionWorkspaceStore } from '@/stores/version-workspace';

const featureTreeStore = useFeatureTreeStore();
const versionWorkspaceStore = useVersionWorkspaceStore();
const {
  featureTreeData,
  featureTreeRevision,
  selectedKeys,
  assemblyEditHighlightedTreeKeys,
  isTreeLoading,
  isFeatureRollbackBarDisabled,
} = storeToRefs(featureTreeStore);

const searchQuery = ref('');
const showContextMenu = ref(false);
const contextMenuX = ref(0);
const contextMenuY = ref(0);
const contextMenuTargetKey = ref('');
const contextMenuNodeType = ref('');
const contextMenuPlaneId = ref('');
const contextMenuRevision = ref(0);
const expandedKeys = ref<string[]>([]);
const panelContentRef = ref<HTMLElement | null>(null);
const isRollbackBarDragging = ref(false);
const rollbackBarDragTargetIndex = ref<number | null>(null);
const rollbackBarDragTargetKey = ref('');
const rollbackBarDragTargetPlacement = ref<'before' | 'after' | null>(null);
let rollbackBarActivePointerId: number | null = null;
let rollbackBarDropTargetRows: FeatureRollbackDropTargetRow[] = [];
let selectedNodeVisibilityRafId: number | null = null;
let selectedNodeVisibilityTrailingRafId: number | null = null;
let panelContentResizeObserver: ResizeObserver | null = null;

const isReadonlyPreview = computed(() => versionWorkspaceStore.isReadonlyPreview === true);
const isRollbackBarInteractionDisabled = computed(() => (
  isReadonlyPreview.value || isFeatureRollbackBarDisabled.value
));
/**
 * 解析 Mate 节点调用提示文案。
 *
 * @param treeKey 设计树节点 key。
 * @param fallbackLabel 节点显示名称。
 * @param node 当前响应式展示节点，诊断字段变化必须直接驱动标签更新。
 * @returns 调用提示文案；非 Mate 节点返回 undefined。
 */
function resolveAssemblyMateCalloutText(
  treeKey: string,
  fallbackLabel: string,
  node: Readonly<Record<string, unknown>>,
): string | undefined {
  const meta = featureTreeStore.featureTreeNodeMap.get(treeKey) as FeatureTreeNodeMeta | undefined;
  if (meta?.kind !== FeatureTreeNodeKind.AssemblyMate || !Array.isArray(meta.mateParticipants)) {
    return undefined;
  }

  const participantText = meta.mateParticipants
    .map((participant, index) => {
      /**
       * SOLIDWORKS 的 Mate 调用信息关注“哪两个组件的哪两个实体”。
       * 这里使用 cad-core 投影好的组件短名和实体标签，避免 UI 从场景对象反推引用。
       */
      const slot = index + 1;
      return `${slot}. ${participant.entityLabel} @ ${participant.componentLabel}`;
    })
    .join('\n');
  // 元数据 Map 不是响应式来源；同一错误状态下只改文本，也必须建立节点字段依赖。
  // 尚未提供展示字段的外部投影沿用元数据；显式 undefined 表示清除，不能回退旧诊断。
  const errorMessage = 'mateErrorMessage' in node ? node.mateErrorMessage : meta.mateErrorMessage;
  const solveStatus = 'mateSolveStatus' in node ? node.mateSolveStatus : meta.mateSolveStatus;
  const diagnosticText = typeof errorMessage === 'string' && errorMessage.trim().length > 0
    ? `错误：${errorMessage.trim()}`
    : (typeof solveStatus === 'string'
      && solveStatus !== 'solved'
      && solveStatus !== 'notRun'
        ? `状态：${solveStatus}`
        : '');

  const body = [
    participantText,
    diagnosticText,
  ].filter((item) => item.length > 0).join('\n');

  return body.length > 0
    ? `${fallbackLabel}\n${body}`
    : undefined;
}

/**
 * 设计树数据变化时归并展开状态。
 *
 * 设计说明：
 * 不能在每次树数据刷新时只保留根节点，否则点击被特征吸收的草图引用会让父特征收缩。
 */
watch(
  [featureTreeData, featureTreeRevision],
  ([nextData]) => {
    // 配合局部增删保留根数组引用，通过投影版本归并展开键，不用于重新挂载 Tree。
    expandedKeys.value = resolveFeatureTreeExpandedKeys(
      nextData,
      expandedKeys.value,
      selectedKeys.value[0] ?? '',
    );
  },
  { immediate: true },
);

watch(
  () => selectedKeys.value[0] ?? '',
  (selectedKey) => {
    if (selectedKey) {
      expandedKeys.value = resolveFeatureTreeExpandedKeys(
        featureTreeData.value,
        expandedKeys.value,
        selectedKey,
      );
    }
    scheduleEnsureSelectedNodeVisible();
  },
  {
    /**
     * 插入零部件后，设计树的选中态可能先在 store 中写入，
     * 而组件稍后才因右侧面板重新显示而挂载。
     * 这里必须在挂载时补偿执行一次滚动逻辑，否则用户会误判为“树没有刷新到新节点”。
     */
    immediate: true,
  },
);

/**
 * 取消仍在排队的选中节点可见性补偿任务。
 *
 * 设计说明：
 * 1. 插入零部件、切换右侧 tab、浏览器缩放时，设计树可能在很短时间内连续触发多次刷新；
 * 2. 若不取消旧任务，旧布局快照对应的滚动会在新布局之后落地，导致滚动位置抖动；
 * 3. 统一在组件内收口调度状态，避免把滚动时序泄漏到 store 或 loader。
 */
function cancelScheduledSelectedNodeVisibilityCheck(): void {
  if (selectedNodeVisibilityRafId !== null) {
    if (typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(selectedNodeVisibilityRafId);
    }
    selectedNodeVisibilityRafId = null;
  }
  if (selectedNodeVisibilityTrailingRafId !== null) {
    if (typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(selectedNodeVisibilityTrailingRafId);
    }
    selectedNodeVisibilityTrailingRafId = null;
  }
}

/**
 * 清理退回控制棒拖拽监听。
 */
function removeRollbackBarPointerListeners(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.removeEventListener('pointermove', handleRollbackBarPointerMove);
  window.removeEventListener('pointerup', handleRollbackBarPointerUp);
  window.removeEventListener('pointercancel', cancelRollbackBarPointerDrag);
}

/**
 * 重置退回控制棒拖拽状态。
 */
function clearRollbackBarPointerDragState(): void {
  removeRollbackBarPointerListeners();
  isRollbackBarDragging.value = false;
  rollbackBarActivePointerId = null;
  rollbackBarDropTargetRows = [];
  rollbackBarDragTargetIndex.value = null;
  rollbackBarDragTargetKey.value = '';
  rollbackBarDragTargetPlacement.value = null;
}

/**
 * 收集当前可见的退回控制棒落放行。
 *
 * @returns FeatureManager 时间线行集合。
 */
function collectRollbackDropTargetRows(): FeatureRollbackDropTargetRow[] {
  const panelContentElement = panelContentRef.value;
  if (!panelContentElement) {
    return [];
  }

  return Array.from(panelContentElement.querySelectorAll<HTMLElement>('[data-tree-key]'))
    .map((nodeElement): FeatureRollbackDropTargetRow | null => {
      const treeKey = nodeElement.dataset.treeKey ?? '';
      const nodeMeta = featureTreeStore.featureTreeNodeMap.get(treeKey);
      if (
        !nodeMeta
        || nodeMeta.kind === FeatureTreeNodeKind.RollbackBar
        || typeof nodeMeta.featureManagerIndex !== 'number'
      ) {
        return null;
      }

      const rect = nodeElement.getBoundingClientRect();
      return {
        treeKey,
        featureManagerIndex: nodeMeta.featureManagerIndex,
        targetTop: rect.top,
        targetHeight: rect.height,
      };
    })
    .filter((row): row is FeatureRollbackDropTargetRow => Boolean(row));
}

/**
 * 刷新退回控制棒拖拽期间使用的落放行缓存。
 *
 * 设计说明：
 * 1. `getBoundingClientRect` 会触发布局读取，不能在 pointermove 高频路径里全量执行；
 * 2. 拖拽开始时的可见树行已经足够支撑同一次拖动的落点判断；
 * 3. 后续若支持拖拽中自动滚动，可在滚动事件里复用这个入口刷新缓存。
 */
function refreshRollbackDropTargetRows(): void {
  rollbackBarDropTargetRows = collectRollbackDropTargetRows();
}

/**
 * 根据当前指针位置刷新退回控制棒候选落点。
 *
 * @param event 指针移动事件。
 */
function updateRollbackBarDragTargetFromPointer(event: PointerEvent): void {
  if (!isRollbackBarDragging.value) {
    return;
  }
  if (
    rollbackBarActivePointerId !== null
    && event.pointerId !== rollbackBarActivePointerId
  ) {
    return;
  }
  const rollbackBarMeta = featureTreeStore.featureTreeNodeMap.get('feature-rollback-bar');
  const dropTarget = resolveFeatureRollbackDropTargetFromRows({
    pointerClientY: event.clientY,
    rows: rollbackBarDropTargetRows,
    totalTimelineItemCount: typeof rollbackBarMeta?.totalTimelineItemCount === 'number'
      ? rollbackBarMeta.totalTimelineItemCount
      : undefined,
  });
  if (!dropTarget) {
    rollbackBarDragTargetIndex.value = null;
    rollbackBarDragTargetKey.value = '';
    rollbackBarDragTargetPlacement.value = null;
    return;
  }

  rollbackBarDragTargetIndex.value = dropTarget.cursorIndex;
  rollbackBarDragTargetKey.value = dropTarget.treeKey;
  rollbackBarDragTargetPlacement.value = dropTarget.placement;
}

/**
 * 处理退回控制棒拖拽移动。
 *
 * @param event 指针移动事件。
 */
function handleRollbackBarPointerMove(event: PointerEvent): void {
  updateRollbackBarDragTargetFromPointer(event);
}

/**
 * 取消退回控制棒拖拽。
 */
function cancelRollbackBarPointerDrag(): void {
  clearRollbackBarPointerDragState();
}

/**
 * 完成退回控制棒拖拽，并将控制棒移动到最终落点。
 *
 * @param event 指针抬起事件。
 */
function handleRollbackBarPointerUp(event: PointerEvent): void {
  if (
    rollbackBarActivePointerId !== null
    && event.pointerId !== rollbackBarActivePointerId
  ) {
    return;
  }

  const targetIndex = rollbackBarDragTargetIndex.value;
  clearRollbackBarPointerDragState();
  if (targetIndex === null) {
    return;
  }

  void featureTreeStore.handleFeatureRollbackBarMoveToIndex(targetIndex);
}

/**
 * 启动退回控制棒 pointer 拖拽。
 *
 * @param event 指针按下事件。
 */
function startRollbackBarPointerDrag(event: PointerEvent): void {
  if (isRollbackBarInteractionDisabled.value) {
    return;
  }
  if (typeof event.button === 'number' && event.button !== 0) {
    return;
  }
  if (typeof window === 'undefined') {
    return;
  }

  /**
   * 先清理旧监听再进入新拖拽，避免异常 pointercancel 丢失时留下重复监听。
   */
  clearRollbackBarPointerDragState();
  isRollbackBarDragging.value = true;
  rollbackBarActivePointerId = typeof event.pointerId === 'number' ? event.pointerId : null;
  refreshRollbackDropTargetRows();
  window.addEventListener('pointermove', handleRollbackBarPointerMove);
  window.addEventListener('pointerup', handleRollbackBarPointerUp);
  window.addEventListener('pointercancel', cancelRollbackBarPointerDrag);
}

/**
 * 保证当前选中节点进入设计树可视区。
 *
 * 设计说明：
 * 1. 这里仍然复用浏览器原生 `scrollIntoView`，保持滚动语义与 Naive Tree 的 DOM 结构解耦；
 * 2. 使用 `nearest` 而非 `start`，避免每次选中都把整棵树跳到顶部，保留用户现有浏览上下文；
 * 3. 若节点尚未渲染或当前无选中项，则静默退出，等待下一次数据/尺寸事件再补偿。
 *
 * @param selectedKey 当前选中的树节点 key。
 */
function ensureSelectedNodeVisible(selectedKey: string): void {
  const targetNode = panelContentRef.value?.querySelector<HTMLElement>(
    `[data-tree-key="${selectedKey}"]`,
  );
  targetNode?.scrollIntoView({
    block: 'nearest',
    inline: 'nearest',
  });
}

/**
 * 调度一次“选中节点可见性补偿”。
 *
 * 设计说明：
 * 1. `nextTick` 只能保证 Vue DOM 提交完成，不能保证浏览器已经完成隐藏 pane 恢复后的布局结算；
 * 2. 这里串联两帧 `requestAnimationFrame`，给浏览器一次完整的样式与布局稳定窗口，
 *    解决“隐藏态选中后切回设计树 / 浏览器缩放后节点仍未进入可视区”的问题；
 * 3. 所有触发源（选中变化、树数据刷新、容器尺寸变化）都复用这个入口，避免散落的滚动逻辑。
 */
function scheduleEnsureSelectedNodeVisible(): void {
  const selectedKey = selectedKeys.value[0] ?? '';
  if (!selectedKey) {
    return;
  }

  cancelScheduledSelectedNodeVisibilityCheck();

  void nextTick(() => {
    if (typeof requestAnimationFrame !== 'function') {
      ensureSelectedNodeVisible(selectedKey);
      return;
    }

    selectedNodeVisibilityRafId = requestAnimationFrame(() => {
      selectedNodeVisibilityRafId = null;
      selectedNodeVisibilityTrailingRafId = requestAnimationFrame(() => {
        selectedNodeVisibilityTrailingRafId = null;
        ensureSelectedNodeVisible(selectedKey);
      });
    });
  });
}

onMounted(() => {
  if (typeof ResizeObserver === 'undefined' || !panelContentRef.value) {
    return;
  }

  /**
   * 右侧 pane 从隐藏切回可见、浏览器缩放、面板高度变化时，
   * 需要再次校正选中节点可视区；否则会出现“节点已选中但仍掉在折叠线下方”的错觉。
   */
  panelContentResizeObserver = new ResizeObserver(() => {
    scheduleEnsureSelectedNodeVisible();
  });
  panelContentResizeObserver.observe(panelContentRef.value);
});

onUnmounted(() => {
  panelContentResizeObserver?.disconnect();
  panelContentResizeObserver = null;
  cancelScheduledSelectedNodeVisibilityCheck();
  clearRollbackBarPointerDragState();
});

const normalizedQuery = computed(() => searchQuery.value.trim().toLowerCase());

//------ 线性零部件阵列节点包围盒移入 开始------
/**
 * 线性零部件阵列运行时节点类型。
 */
enum LinearComponentPatternRuntimeNodeType {
  /** 阵列分组节点。 */
  Group = 'LinearPatternRuntimeGroup',
  /** 阵列零件节点。 */
  Part = 'LinearPatternRuntimePart',
}

//------ 线性阵列来源对象粉色高亮 开始------
/**
 * 线性阵列分组点击时需要传递的目标集合。
 */
interface LinearComponentPatternTreeSelectionTargets {
  /** 复制体对象 ID 列表，作为真实主选对象写入 cad-core 选择状态。 */
  readonly partObjectIds: string[];
  /** 源对象 ID 列表，仅用于 cad-core 视觉层显示粉色来源高亮。 */
  readonly sourceObjectIds: string[];
  /** 传给 cad-core 选择链路的 GLB 节点语义角色。 */
  readonly glbNodeRole: GlbNodeRole;
}
//------ 线性阵列来源对象粉色高亮 结束------

//------ 线性阵列子零件节点选中高亮 开始------
/**
 * 树节点搜索结果。
 */
interface FeatureTreeNodeSearchResult {
  /** 命中的树节点。 */
  readonly node: TreeOption;
  /** 命中节点的父节点；根节点返回 `null`。 */
  readonly parentNode: TreeOption | null;
}
//------ 线性阵列子零件节点选中高亮 结束------

/**
 * 判断节点是否为线性零部件阵列分组节点。
 *
 * @param node 树节点。
 * @returns 若节点是线性零部件阵列分组，则返回 `true`。
 */
function isLinearComponentPatternGroupNode(node: Record<string, unknown>): boolean {
  return node.linearComponentPatternRuntimeNodeType === LinearComponentPatternRuntimeNodeType.Group;
}

/**
 * 判断节点是否为线性零部件阵列分组下的零件节点。
 *
 * @param node 树节点。
 * @returns 若节点代表阵列分组下的直接零件，则返回 `true`。
 */
function isLinearComponentPatternPartNode(node: Record<string, unknown>): boolean {
  if (node.linearComponentPatternRuntimeNodeType === LinearComponentPatternRuntimeNodeType.Part) {
    return true;
  }

  /**
   * 来源零件可正常回查时，运行时阵列零件会保留来源 nodeType，
   * 不能依赖 `LinearPatternRuntimePart` 标记；此时直接子节点的 glb-node + refObjectId
   * 才是设计树零件 hover 的稳定锚点。
   */
  return node.type === 'glb-node' && typeof node.refObjectId === 'string';
}

//------ 线性阵列子零件节点选中高亮 开始------
/**
 * 解析树节点携带的场景对象 ID。
 *
 * @param node 树节点。
 * @returns 修剪后的场景对象 ID；无效时返回空字符串。
 */
function resolveTreeNodeRefObjectId(node: Record<string, unknown>): string {
  return typeof node.refObjectId === 'string'
    ? node.refObjectId.trim()
    : '';
}

/**
 * 判断当前节点是否是可直接选中的线性阵列子零件节点。
 *
 * 设计说明：
 * 1. 运行时阵列 Part 标记是最稳定的识别条件；
 * 2. 部分同步链路会把子节点保留为普通 `glb-node`，此时只允许“线性阵列分组的直接子节点”兜底命中；
 * 3. 不把任意带 `refObjectId` 的 GLB 节点纳入该路径，避免普通导入模型节点误走阵列选择逻辑。
 *
 * @param node 当前树节点。
 * @param parentNode 当前树节点的父节点。
 * @returns 若当前节点是线性阵列子零件节点，则返回 `true`。
 */
function isLinearComponentPatternSelectablePartNode(
  node: TreeOption,
  parentNode: TreeOption | null,
): boolean {
  const currentNode = node as Record<string, unknown>;
  if (currentNode.linearComponentPatternRuntimeNodeType === LinearComponentPatternRuntimeNodeType.Part) {
    return true;
  }

  return Boolean(parentNode)
    && isLinearComponentPatternGroupNode(parentNode as Record<string, unknown>)
    && isLinearComponentPatternPartNode(currentNode);
}
//------ 线性阵列子零件节点选中高亮 结束------

/**
 * 收集线性零部件阵列分组下所有直接零件对象 ID。
 *
 * 设计说明：
 * 1. 阵列分组自身没有几何选择锚点，真正需要显示包围盒的是其下每个阵列零件；
 * 2. 这里只收集分组直接子节点，避免把实体、材质、基准面等孙级节点重复加入；
 * 3. 使用 Set 去重，保证未来同一零件节点被重复投影时仍保持稳定输出。
 *
 * @param nodes 子节点列表。
 * @returns 零件对象 ID 列表。
 */
function collectLinearComponentPatternPartObjectIds(nodes: readonly TreeOption[] | undefined): string[] {
  const objectIdSet = new Set<string>();

  nodes?.forEach((node) => {
    const currentNode = node as Record<string, unknown>;
    const refObjectId = resolveTreeNodeRefObjectId(currentNode);
    if (isLinearComponentPatternPartNode(currentNode) && refObjectId.length > 0) {
      objectIdSet.add(refObjectId);
    }
  });

  return [...objectIdSet];
}

//------ 线性阵列来源对象粉色高亮 开始------
/**
 * 从树节点字段中读取字符串数组。
 *
 * 设计说明：
 * 1. Naive TreeOption 是开放对象，运行时阵列字段由投影层写入；
 * 2. 组件层只接受非空字符串，避免把异常节点数据继续传给 cad-core；
 * 3. 使用 Set 去重，保证同一源对象生成多个复制体时只触发一次来源高亮。
 *
 * @param node 树节点。
 * @param fieldName 字符串数组字段名。
 * @returns 去重后的字符串数组。
 */
function readTreeNodeStringArrayField(
  node: Record<string, unknown>,
  fieldName: string,
): string[] {
  const rawValue = node[fieldName];
  if (!Array.isArray(rawValue)) {
    return [];
  }

  const valueSet = new Set<string>();
  rawValue.forEach((item) => {
    if (typeof item !== 'string') {
      return;
    }
    const normalizedItem = item.trim();
    if (normalizedItem.length > 0) {
      valueSet.add(normalizedItem);
    }
  });

  return [...valueSet];
}

/**
 * 从线性阵列分组树节点收集真实选中目标与来源高亮目标。
 *
 * 设计说明：
 * 1. 分组节点点击后，复制体 A1/A2 仍然是真实 selectedObjects；
 * 2. 源对象 A 只通过 `sourceObjectIds` 进入视觉层，用于显示 `#ff8bd0` 来源提示；
 * 3. 若旧投影没有分组级来源列表，则返回空来源列表，保持兼容而不猜测。
 *
 * @param node 线性阵列分组树节点。
 * @returns 分组点击目标集合。
 */
function collectLinearComponentPatternSelectionTargets(
  node: TreeOption,
): LinearComponentPatternTreeSelectionTargets {
  const currentNode = node as Record<string, unknown>;

  return {
    partObjectIds: collectLinearComponentPatternPartObjectIds(node.children),
    sourceObjectIds: readTreeNodeStringArrayField(
      currentNode,
      'linearComponentPatternSourceObjectIds',
    ),
    glbNodeRole: 'group',
  };
}
//------ 线性阵列来源对象粉色高亮 结束------

//------ 线性阵列子零件节点选中高亮 开始------
/**
 * 从线性阵列子零件节点收集单个复制体选择目标。
 *
 * 设计说明：
 * 1. 子节点点击只代表用户要选中 A1 或 A2 自身，不应附带源对象 A 的粉色来源高亮；
 * 2. 直接读取节点投影携带的 `refObjectId`，避免普通 key 选择链路无法反查运行时复制体；
 * 3. 仍复用多目标选择动作，保证单选与分组选中进入同一条 cad-core 高亮链路。
 *
 * @param node 线性阵列子零件树节点。
 * @returns 子零件点击目标集合。
 */
function collectLinearComponentPatternPartSelectionTargets(
  node: TreeOption,
): LinearComponentPatternTreeSelectionTargets {
  const refObjectId = resolveTreeNodeRefObjectId(node as Record<string, unknown>);

  return {
    partObjectIds: refObjectId.length > 0 ? [refObjectId] : [],
    sourceObjectIds: [],
    glbNodeRole: 'part',
  };
}
//------ 线性阵列子零件节点选中高亮 结束------

/**
 * 在树节点列表中查找指定 key 的节点。
 *
 * @param nodes 节点列表。
 * @param key 目标节点 key。
 * @param parentNode 当前递归层级的父节点。
 * @returns 命中的树节点与父节点；未命中时返回 `null`。
 */
function findTreeNodeByKey(
  nodes: readonly TreeOption[],
  key: string,
  parentNode: TreeOption | null = null,
): FeatureTreeNodeSearchResult | null {
  for (const node of nodes) {
    if (getFeatureTreeNodeKey(node) === key) {
      return {
        node,
        parentNode,
      };
    }
    const matchedChild = Array.isArray(node.children)
      ? findTreeNodeByKey(node.children, key, node)
      : null;
    if (matchedChild) {
      return matchedChild;
    }
  }

  return null;
}

/**
 * 判断节点是否为分组节点。
 *
 * @param node 树节点。
 * @returns 是否为分组节点。
 */
function isGroupNode(node: TreeOption): boolean {
  return (node as { type?: string }).type === 'group';
}

/**
 * 判断节点是否为退回控制棒。
 *
 * @param node 树节点。
 * @returns 是否为退回控制棒节点。
 */
function isRollbackBarNode(node: TreeOption | null): boolean {
  return (node as { type?: string } | null)?.type === 'rollback-bar';
}

/**
 * 判断节点是否存在可递归搜索的子节点。
 *
 * @param node 树节点。
 * @returns 是否有子节点。
 */
function hasSearchableChildren(node: TreeOption): boolean {
  return Array.isArray(node.children) && node.children.length > 0;
}

/**
 * 读取节点标签文本。
 *
 * @param node 树节点。
 * @returns 标签文本。
 */
function getNodeLabelText(node: TreeOption): string {
  if (typeof node.label === 'string') {
    return node.label;
  }
  return String(node.label ?? '');
}

/**
 * 统计匹配搜索词的叶子节点数量。
 *
 * @param nodes 节点列表。
 * @param query 搜索词。
 * @returns 命中数量。
 */
function countMatchingNodes(nodes: TreeOption[], query: string): number {
  return nodes.reduce((count, node) => {
    const selfMatched = getNodeLabelText(node).toLowerCase().includes(query) ? 1 : 0;
    const childMatched = hasSearchableChildren(node)
      ? countMatchingNodes(node.children || [], query)
      : 0;
    return count + selfMatched + childMatched;
  }, 0);
}

/**
 * 根据搜索词过滤树节点。
 *
 * @param nodes 节点列表。
 * @param query 搜索词。
 * @returns 过滤后的节点列表。
 */
function filterTreeNodes(nodes: TreeOption[], query: string): TreeOption[] {
  return nodes
    .map((node) => {
      const filteredChildren = hasSearchableChildren(node)
        ? filterTreeNodes(node.children || [], query)
        : [];
      const selfMatched = getNodeLabelText(node).toLowerCase().includes(query);
      if (filteredChildren.length > 0 || (isGroupNode(node) && selfMatched)) {
        return {
          ...node,
          children: filteredChildren,
        };
      }
      return selfMatched ? node : null;
    })
    .filter(Boolean) as TreeOption[];
}

const hasSearchQuery = computed(() => normalizedQuery.value.length > 0);
const matchCount = computed(() => (
  hasSearchQuery.value ? countMatchingNodes(featureTreeData.value, normalizedQuery.value) : 0
));

const displayedTreeData = computed(() => {
  // 局部结构变更保留 store 的根数组；NaiveUI 用顶层 data 发布识别数据更新并区分展开动画。
  // 只复制根列表，不复制模型节点，也不以版本号重挂载 Tree；纯诊断不会增加此版本。
  void featureTreeRevision.value;
  return hasSearchQuery.value
    ? filterTreeNodes(featureTreeData.value, normalizedQuery.value)
    : [...featureTreeData.value];
});

watch(
  () => displayedTreeData.value,
  () => {
    scheduleEnsureSelectedNodeVisible();
  },
);


const showTree = computed(() => (
  hasSearchQuery.value ? matchCount.value > 0 : featureTreeData.value.length > 0
));

const emptyDescription = computed(() => (
  hasSearchQuery.value ? '未找到匹配项' : '暂无特征对象'
));

/**
 * 渲染设计树语义图标。
 *
 * @param iconType 图标语义类型。
 * @param size 图标尺寸。
 * @returns Carbon 图标节点。
 */
function renderFeatureTreeIcon(iconType: string, size = 14) {
  return h(FeatureTreeNodeIcon, { iconType, size });
}

/**
 * 渲染设计树状态图标。
 *
 * @param iconType 状态图标语义类型。
 * @param size 状态图标尺寸。
 * @returns 状态图标节点。
 */
function renderFeatureTreeStatusIcon(iconType: string, size = 12) {
  return renderFeatureTreeIcon(iconType, size);
}

/**
 * 渲染树节点标签。
 *
 * @param input 渲染输入。
 * @returns 渲染结果。
 */
function renderTreeNodeLabel(input: { option: TreeOption }) {
  const node = input.option as Record<string, unknown>;
  if (isRollbackBarNode(input.option)) {
    return h(FeatureRollbackBarLabel, {
      active: node.statusRolledBack === true,
      disabled: isRollbackBarInteractionDisabled.value,
      onRollbackDragStart: startRollbackBarPointerDrag,
    });
  }

  const treeKey = String(node.key ?? '');
  const iconType = String(node.iconType ?? node.type ?? '');
  const labelText = String(node.label ?? '');
  const iconNode = renderFeatureTreeIcon(iconType);
  const isAssemblyEditHighlighted = assemblyEditHighlightedTreeKeys.value.has(treeKey);
  const assemblyMateCalloutText = resolveAssemblyMateCalloutText(treeKey, labelText, node);
  const hasError = node.statusError === true;
  const hasWarning = node.statusWarning === true && !hasError;
  /*
   * SOLIDWORKS 把 rebuild error/warning 作为原类型图标的前置状态，而不是普通尾随状态。
   * error 优先可以避免同一节点同时出现两个互相竞争的诊断符号。
   */
  const leadingDiagnosticNode = hasError || hasWarning
    ? h('span', {
      class: [
        'tree-node-diagnostic',
        {
          'tree-node-diagnostic--error': hasError,
          'tree-node-diagnostic--warning': hasWarning,
        },
      ],
      title: String(node.warningMessage ?? ''),
    }, [
      renderFeatureTreeStatusIcon(hasError ? 'status-error' : 'status-warning', 14),
    ])
    : null;
  const statusNodes = [
    node.statusHidden ? renderFeatureTreeStatusIcon('status-hidden') : null,
    node.statusSuppressed ? renderFeatureTreeStatusIcon('status-suppressed') : null,
    node.statusShared ? renderFeatureTreeStatusIcon('status-shared') : null,
    node.statusReference ? renderFeatureTreeStatusIcon('status-reference') : null,
  ].filter(Boolean);

  return h('span', { class: 'tree-node-label-wrap' }, [
    h('span', { class: 'tree-node-leading-icons' }, [
      leadingDiagnosticNode,
      h('span', { class: 'tree-node-icon' }, [iconNode]),
    ]),
    h('span', {
      class: [
        'tree-node-label-text',
        { 'tree-node-label-text--assembly-edit': isAssemblyEditHighlighted },
        { 'tree-node-label-text--rolled-back': node.statusRolledBack === true },
        { 'tree-node-label-text--error': hasError },
        { 'tree-node-label-text--warning': hasWarning },
      ],
      title: String(node.warningMessage ?? assemblyMateCalloutText ?? ''),
      'data-mate-callout': assemblyMateCalloutText,
    }, labelText),
    statusNodes.length > 0 ? h('span', { class: 'tree-node-status' }, statusNodes) : null,
  ]);
}

/**
 * 处理树节点选中。
 *
 * @param keys 选中节点键列表。
 */
function handleSelect(keys: string[]): void {
  //------ 线性零部件阵列节点选中高亮 开始------
  const selectedKey = keys[0] ?? '';
  const selectedNodeMeta = selectedKey ? featureTreeStore.featureTreeNodeMap.get(selectedKey) : null;
  if (selectedNodeMeta?.kind === FeatureTreeNodeKind.RollbackBar) {
    /**
     * 退回控制棒是 FeatureManager 分隔控件，不是可选模型节点。
     * 这里在统一选择入口截断，避免 click 或拖拽结束后的 click 污染场景选择。
     */
    return;
  }
  const selectedNodeSearchResult = selectedKey ? findTreeNodeByKey(displayedTreeData.value, selectedKey) : null;
  const selectedNode = selectedNodeSearchResult?.node ?? null;
  if (selectedNode && isLinearComponentPatternGroupNode(selectedNode as Record<string, unknown>)) {
    const linearComponentPatternTargets = collectLinearComponentPatternSelectionTargets(selectedNode);
    if (linearComponentPatternTargets.partObjectIds.length > 0) {
      featureTreeStore.handleFeatureNodeSelectTargets({
        key: selectedKey,
        refObjectIds: linearComponentPatternTargets.partObjectIds,
        sourceObjectIds: linearComponentPatternTargets.sourceObjectIds,
        glbNodeRole: linearComponentPatternTargets.glbNodeRole,
      });
      return;
    }
  }
  //------ 线性阵列子零件节点选中高亮 开始------
  if (selectedNode && isLinearComponentPatternSelectablePartNode(
    selectedNode,
    selectedNodeSearchResult?.parentNode ?? null,
  )) {
    const linearComponentPatternTargets = collectLinearComponentPatternPartSelectionTargets(selectedNode);
    if (linearComponentPatternTargets.partObjectIds.length > 0) {
      featureTreeStore.handleFeatureNodeSelectTargets({
        key: selectedKey,
        refObjectIds: linearComponentPatternTargets.partObjectIds,
        glbNodeRole: linearComponentPatternTargets.glbNodeRole,
      });
      return;
    }
  }
  //------ 线性阵列子零件节点选中高亮 结束------
  //------ 线性零部件阵列节点选中高亮 结束------
  void featureTreeStore.handleFeatureSelect(keys);
}

/**
 * 处理展开键变化。
 *
 * @param keys 展开键集合。
 */
function handleExpandedKeysChange(keys: Array<string | number>): void {
  expandedKeys.value = keys.map((key) => String(key));
}

/**
 * 构建树节点事件。
 *
 * @param input 树节点输入。
 * @returns 节点事件与属性。
 */
function buildTreeNodeProps(input: { option: TreeOption }) {
  const option = input.option as Record<string, unknown>;
  const nodeType = String(option.type ?? '');
  const treeKey = String(option.key ?? '');
  const refObjectId = typeof option.refObjectId === 'string' ? option.refObjectId : '';
  const isRollbackBar = nodeType === 'rollback-bar';
  const isDisabledRollbackBar = isRollbackBar && isRollbackBarInteractionDisabled.value;
  const isRollbackDropTarget = rollbackBarDragTargetKey.value === treeKey;
  //------ 线性零部件阵列节点包围盒移入 开始------
  const linearComponentPatternPartObjectIds = isLinearComponentPatternGroupNode(option)
    ? collectLinearComponentPatternPartObjectIds(input.option.children)
    : [];
  //------ 线性零部件阵列节点包围盒移入 结束------
  const planeKey = treeKey;
  const isDatumPlane = nodeType === 'datum-plane';
  const isDatumAxis = nodeType === 'datum-axis';
  const isDatumPoint = nodeType === 'datum-point' || nodeType === 'origin';
  const isDatumReference = isDatumPlane || isDatumAxis || isDatumPoint;
  const isGlbDatumSlot = nodeType === 'glb-datum-slot';
  // .puppy 实体的真实场景锚点可能只存在于节点元数据中，组件层必须把 key 交给 store 统一解析。
  const shouldDispatchImportedModelHover = refObjectId.length > 0
    || isGlbDatumSlot
    || nodeType === FeatureTreeNodeKind.GlbNode;
  const isAssemblyMate = nodeType === 'assembly-mate';
  const planeId = isDatumPlane ? planeKey.replace('datum-plane-', '') : '';

  return {
    'data-tree-key': treeKey,
    class: [
      isRollbackBar ? 'feature-tree-node--rollback-bar' : '',
      isDisabledRollbackBar ? 'feature-tree-node--rollback-bar-disabled' : '',
      option.statusRolledBack === true ? 'feature-tree-node--rolled-back' : '',
      isRollbackDropTarget && rollbackBarDragTargetPlacement.value === 'before'
        ? 'feature-tree-node--rollback-drop-before'
        : '',
      isRollbackDropTarget && rollbackBarDragTargetPlacement.value === 'after'
        ? 'feature-tree-node--rollback-drop-after'
        : '',
    ].filter(Boolean).join(' '),
    tabindex: isRollbackBar ? 0 : undefined,
    onKeydown(event: KeyboardEvent): void {
      if (!isRollbackBar) {
        return;
      }
      if (isDisabledRollbackBar) {
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        void featureTreeStore.handleFeatureRollbackBarMoveUp();
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        void featureTreeStore.handleFeatureRollbackBarMoveDown();
      }
    },
    onContextmenu(event: MouseEvent): void {
      /**
       * Naive Tree 的子节点 DOM 会嵌套在父节点结构内。Mate 节点右键如果继续冒泡，
       * 父装配体节点会覆盖 contextMenuTargetKey，随后“删除”会误走普通节点删除链路。
       */
      event.stopPropagation();
      event.preventDefault();
      if (isDisabledRollbackBar) {
        showContextMenu.value = false;
        return;
      }
      contextMenuX.value = event.clientX;
      contextMenuY.value = event.clientY;
      contextMenuTargetKey.value = planeKey;
      contextMenuNodeType.value = nodeType;
      contextMenuPlaneId.value = planeId;
      contextMenuRevision.value += 1;
      showContextMenu.value = true;
    },
    onMouseenter(): void {
      //------ 线性零部件阵列节点包围盒移入 开始------
      if (linearComponentPatternPartObjectIds.length > 0) {
        featureTreeStore.handleFeatureNodeHoverTargets({
          refObjectIds: linearComponentPatternPartObjectIds,
          hovered: true,
        });
        return;
      }
      //------ 线性零部件阵列节点包围盒移入 结束------
      if (isAssemblyMate) {
        featureTreeStore.handleAssemblyMateNodeHover(planeKey, true);
        return;
      }
      if (shouldDispatchImportedModelHover) {
        featureTreeStore.handleFeatureNodeHover({
          key: planeKey,
          refObjectId,
          hovered: true,
        });
      }
      if (isDatumReference) {
        featureTreeStore.handleDatumReferenceHover(planeKey, true);
      }
    },
    onMouseleave(): void {
      //------ 线性零部件阵列节点包围盒移入 开始------
      if (linearComponentPatternPartObjectIds.length > 0) {
        featureTreeStore.handleFeatureNodeHoverTargets({
          refObjectIds: linearComponentPatternPartObjectIds,
          hovered: false,
        });
        return;
      }
      //------ 线性零部件阵列节点包围盒移入 结束------
      if (isAssemblyMate) {
        featureTreeStore.handleAssemblyMateNodeHover(planeKey, false);
        return;
      }
      if (shouldDispatchImportedModelHover) {
        featureTreeStore.handleFeatureNodeHover({
          key: planeKey,
          refObjectId,
          hovered: false,
        });
      }
      if (isDatumReference) {
        featureTreeStore.handleDatumReferenceHover(planeKey, false);
      }
    },
  };
}


</script>

<style scoped>
.panel-content {
  flex: 1;
  position: relative;
  overflow-y: auto;
  padding: 8px;
  min-height: 0;
}

.feature-tree-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 60px 0;
  color: var(--n-text-color-3, #6b7280);
  font-size: 12px;
}

.feature-tree-loading-overlay {
  position: absolute;
  inset: 8px;
  background: rgba(15, 17, 21, 0.08);
  backdrop-filter: blur(1px);
  z-index: 1;
}

.feature-tree-loading-text {
  white-space: nowrap;
}

.panel-search {
  padding: 0 16px 8px;
}

:deep(.tree-node-label-wrap) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

:deep(.tree-node-icon) {
  width: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

:deep(.tree-node-leading-icons) {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex: 0 0 auto;
}

:deep(.tree-node-diagnostic) {
  width: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 14px;
  opacity: 1;
}

:deep(.tree-node-label-text--assembly-edit) {
  color: var(--n-primary-color, var(--puppy-primary));
}

:deep(.tree-node-label-text--warning) {
  color: #f59e0b;
}

:deep(.tree-node-label-text--error) {
  color: #ef4444;
}

:deep(.n-tree-node-content--selected .tree-node-label-text--warning),
:deep(.n-tree-node-content--selected .tree-node-label-text--error) {
  color: inherit;
}

:deep(.tree-node-label-text--rolled-back) {
  color: var(--n-text-color-disabled, #9ca3af);
}

:deep(.tree-node-status) {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  opacity: 0.75;
  font-size: 12px;
}

:deep(.feature-tree-node--rolled-back) {
  opacity: 0.55;
}

:deep(.feature-tree-node--rollback-drop-before),
:deep(.feature-tree-node--rollback-drop-after) {
  position: relative;
}

:deep(.feature-tree-node--rollback-drop-before::before),
:deep(.feature-tree-node--rollback-drop-after::after) {
  content: '';
  position: absolute;
  left: 18px;
  right: 4px;
  height: 2px;
  background: var(--n-primary-color, var(--puppy-primary));
  pointer-events: none;
}

:deep(.feature-tree-node--rollback-drop-before::before) {
  top: 0;
}

:deep(.feature-tree-node--rollback-drop-after::after) {
  bottom: 0;
}

</style>
